import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, User, Mail, Phone, Lock, Camera, Loader2, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { toast } from 'sonner';
import EnhancedAppHeader from '@/components/EnhancedAppHeader';

export default function Profile() {
  const navigate = useNavigate();
  const { user, updateUser, activeOrg } = useAuth();
  const fileInputRef = useRef(null);
  
  // Profile State
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [weddingsRemaining, setWeddingsRemaining] = useState(0);
  
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Load credits from active organization
  useEffect(() => {
    const loadCredits = async () => {
      if (activeOrg?.credits !== undefined) {
        setWeddingsRemaining(activeOrg.credits);
      } else {
        // Fallback: fetch organizations to get credits
        try {
          const orgs = await authService.getOrganizations();
          const currentOrg = orgs.find(org => org.id === activeOrg?.id) || orgs[0];
          if (currentOrg?.credits !== undefined) {
            setWeddingsRemaining(currentOrg.credits);
          }
        } catch (error) {
          console.error('Failed to load credits:', error);
        }
      }
    };

    loadCredits();
  }, [activeOrg]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // In production, upload to server and get URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result);
        toast.success('Profile photo updated!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Call API to update profile (email cannot be changed by user)
      const updatedUser = await authService.updateProfile({
        name,
        phone,
        avatar
        // Note: email is not included - users cannot change their own email
      });
      
      // Update local storage and context
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Refresh user in context
      if (updateUser) {
        await updateUser();
        // Update local state from refreshed user
        const refreshedUser = await authService.getCurrentUser();
        if (refreshedUser) {
          setName(refreshedUser.name);
          setEmail(refreshedUser.email);
          setPhone(refreshedUser.phone || '');
          setAvatar(refreshedUser.avatar || '');
        }
      }
      
      // Refresh organizations to get updated credits
      try {
        const orgs = await authService.getOrganizations();
        const currentOrg = orgs.find(org => org.id === activeOrg?.id) || orgs[0];
        if (currentOrg?.credits !== undefined) {
          setWeddingsRemaining(currentOrg.credits);
        }
      } catch (error) {
        console.error('Failed to refresh credits:', error);
      }
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      // In production, call API
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <EnhancedAppHeader />
      
      <div className="container mx-auto p-4 sm:p-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text">Edit Profile</h1>
          </div>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Profile Information
            </h2>

            <form onSubmit={handleProfileUpdate} className="space-y-6">
              {/* Profile Photo */}
              <div className="flex flex-col items-center gap-4 pb-6 border-b">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-4 border-primary/20">
                    <AvatarImage src={avatar} alt={name} />
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {getInitials(name)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-2 -right-2 p-2 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium">{name}</p>
                </div>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1.5"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="mt-1.5"
                  disabled
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  className="mt-1.5"
                />
              </div>

              {/* Weddings Remaining (Credits) */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950 rounded-lg p-4 border-2 border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-amber-900 dark:text-amber-100 font-semibold">
                      Weddings Remaining
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                      {weddingsRemaining}
                    </div>
                    <CreditCard className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full gradient-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Profile'
                )}
              </Button>
            </form>
          </Card>

          {/* Change Password */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Change Password
            </h2>

            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="mt-1.5"
                  required
                />
              </div>

              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="mt-1.5"
                  required
                  minLength={8}
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className="mt-1.5"
                  required
                  minLength={8}
                />
              </div>

              <Button type="submit" className="w-full gradient-primary" disabled={passwordLoading}>
                {passwordLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </form>

          </Card>
        </div>
      </div>
    </div>
  );
}
